import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useNigoAnalysisStateMachine,
  NigoAnalysisState,
} from '@client/hooks/useNigoAnalysisStateMachine';
import { useOpenAiCompletion } from '@client/hooks/useOpenAiCompletion';
import { useClientPolicyApplication } from '@client/hooks/useClientPolicyApplication';
import { useFormConditionals } from '@client/hooks/useFormConditionals';
import { postPolicyApplicationFormNigoRequest } from '@client/model';
import {
  applyCompositeFields,
  evaluateNigoForPolicy,
  FormParameterValues,
  FundingPolicyType,
  logError,
  logInfo,
} from '@shared';
import { Position, useNavigation } from '@client';
import { cleanFormDataForHiddenQuestions } from '@client/utils/formDataUtils';
import { useToast } from '@client/components/hooks/use-toast';
import { savePolicyApplicationFormSubmission } from '@client/hooks/usePolicyApplicationFormSubmission';

// Question refs to filter out from NIGO analysis to avoid flagging wrong issues.
// Adding these to AI prompt or providing context would be longer and unreliable.
const FILTERED_QUESTION_REFS: string[] = [
  // Add question refs here that should be excluded from NIGO analysis

  //Lincoln
  'ltcNotSuitableAcknowledgement',
];

interface NigoAnalysisContextValue {
  state: NigoAnalysisState;
  data: {
    aiResult: ParsedNigoResponse | null;
    ruleResult: RuleResult[];
    resolvedIssues: RuleResult[];
    lastRunTimestamp: Date | null;
    metadata: {
      formDefinition: PolicyFormDefinition;
      formData: PolicyFormSubmissionData;
      showNigoAnalysisView: boolean;
      parameters: FormParameterValues;
      editedQuestions: Set<string>;
      hasCompletedFormOnce: boolean;
      awaitingRecheck: Set<string>;
      analyzingAwaitingRecheck: Set<string>;
    };
  };
  actions: {
    rerunAnalysis: () => void;
    handleSubmissionChange: (questionRef: string) => (value: PolicyFormSubmissionDataValue) => void;
    setShowNigoAnalysisView: (show: boolean) => void;
    onEditedQuestionsChange: (questions: Set<string>) => void;
    handleJumpToQuestion: (questionRef: string) => void;
    handleRerunAnalysis: () => void;
    handleReviewCompleteAndSubmit: () => void;
    toggleIssue: (key: string) => void;
    setAccordionValue: (value: string[]) => void;
    handleSaveChanges: (issueKey: string, relatedQuestions: PolicyFormQuestion[]) => void;
    handleCancelChanges: (relatedQuestions: PolicyFormQuestion[]) => void;
  };
  ui: {
    isLoading: boolean;
    error: string | null;
    expandedIssues: Set<string>;
    accordionValue: string[];
  };
}

const NigoAnalysisContext = createContext<NigoAnalysisContextValue | null>(null);

export function useNigoAnalysisContext() {
  const context = useContext(NigoAnalysisContext);
  if (!context) {
    throw new Error('useNigoAnalysisContext must be used within a NigoAnalysisProvider');
  }
  return context;
}

interface NigoAnalysisProviderProps {
  children: ReactNode;
  showNigoAnalysisView: boolean;
  setShowNigoAnalysisView: (show: boolean) => void;
  parameters: FormParameterValues;
}

const parseAiResponse = (json: string): ParsedNigoResponse | undefined => {
  try {
    const parsed = JSON.parse(json) as ParsedNigoResponse;
    return parsed;
  } catch (err) {
    logError('NigoAnalysisContext', 'Failed to parse AI response', { jsonResponse: json }, err);
    return undefined;
  }
};

const compareRuleResults = (
  currentResults: RuleResult[],
  previousResults: RuleResult[],
  currentResolvedIssues: RuleResult[],
): { newResolvedIssues: RuleResult[]; reinstatedIssues: Set<string> } => {
  const currentIssueKeys = new Set(
    currentResults.map(rule => `${rule.code || ''}:${rule.refs?.join(',') || ''}`),
  );

  const newResolvedIssues: RuleResult[] = [];
  const reinstatedIssues = new Set<string>();

  for (const prevRule of previousResults) {
    const key = `${prevRule.code || ''}:${prevRule.refs?.join(',') || ''}`;
    if (!currentIssueKeys.has(key)) {
      newResolvedIssues.push(prevRule);
    }
  }

  for (const resolvedRule of currentResolvedIssues) {
    const key = `${resolvedRule.code || ''}:${resolvedRule.refs?.join(',') || ''}`;
    if (currentIssueKeys.has(key)) {
      reinstatedIssues.add(key);
    }
  }

  return { newResolvedIssues, reinstatedIssues };
};

export function NigoAnalysisProvider({
  children,
  showNigoAnalysisView,
  parameters,
  setShowNigoAnalysisView,
}: NigoAnalysisProviderProps) {
  const { clientId, clientPolicyApplicationId, formId, forClientId } = useParams();
  const { state: formState, formDefinition, actions: navigationActions } = useNavigation();
  const { steps, underwritingFormId, applicationFormId, policyType, policyApplication } =
    useClientPolicyApplication();
  const { state, actions, computed } = useNigoAnalysisStateMachine();
  const [hasCompletedFormOnce, setHasCompletedFormOnce] = useState(false);
  const [editedQuestions, setEditedQuestions] = useState<Set<string>>(new Set());
  const [awaitingRecheck, setAwaitingRecheck] = useState<Set<string>>(new Set());
  const [expandedIssues, setexpandedIssues] = useState<Set<string>>(new Set());
  const [accordionValue, setAccordionValue] = useState<string[]>(['critical']);
  const [analyzingAwaitingRecheck, setAnalyzingAwaitingRecheck] = useState<Set<string>>(new Set());
  const has70PercentAnalysisTriggered = useRef(false);
  const has100PercentAnalysisTriggered = useRef(false);
  const analyzingQuestionsRef = useRef<Set<string>>(new Set());
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  const conditionalService = useFormConditionals(formDefinition, formState.formData, {});

  // Determine current step based on formId
  const currentStep = useMemo(() => {
    if (formId === applicationFormId) {
      return steps?.find(step => step.stepType === 'application-form');
    }
    if (formId === underwritingFormId) {
      return steps?.find(step => step.stepType === 'pre-underwriting-form');
    }
    return undefined;
  }, [formId, applicationFormId, underwritingFormId, steps]);

  // Extract NIGO data from currentStep if it exists
  useEffect(() => {
    const stepForm = currentStep?.clientPolicyApplicationForm;
    const storedNigoAnalysis = stepForm?.nigoAnalysis?.result;

    if (storedNigoAnalysis) {
      if (storedNigoAnalysis.has70PercentTriggered) {
        has70PercentAnalysisTriggered.current = true;
      }
      if (storedNigoAnalysis.has100PercentTriggered) {
        has100PercentAnalysisTriggered.current = true;
      }

      actions.loadFromDB({
        ruleResult: storedNigoAnalysis.ruleResult,
        aiResult: storedNigoAnalysis.aiResult,
        resolvedIssues: storedNigoAnalysis.resolvedIssues,
        lastRunAt: storedNigoAnalysis.lastRunAt
          ? new Date(storedNigoAnalysis.lastRunAt)
          : undefined,
      });
    }
  }, [currentStep?.clientPolicyApplicationForm?.clientPolicyApplicationFormId, actions]);

  useEffect(() => {
    if (has100PercentAnalysisTriggered.current && !hasCompletedFormOnce) {
      setHasCompletedFormOnce(true);
    }
  }, [hasCompletedFormOnce, has100PercentAnalysisTriggered.current]);

  const saveNigoAnalysis = useCallback(
    (aiResult?: ParsedNigoResponse) => {
      if (!clientId || !currentStep?.clientPolicyApplicationForm) {
        return;
      }

      const { clientPolicyApplicationFormId } = currentStep.clientPolicyApplicationForm;
      if (!clientPolicyApplicationFormId) {
        return;
      }

      actions.startSaving();

      const nigoAnalysisResult: NigoAnalysisResult = {
        result: {
          ruleResult: state.results.ruleResult,
          aiResult: aiResult ?? computed.currentAiResult,
          resolvedIssues: state.results.resolvedIssues,
          lastRunAt: state.meta.lastRunTimestamp || new Date(),
          has70PercentTriggered: has70PercentAnalysisTriggered.current,
          has100PercentTriggered: has100PercentAnalysisTriggered.current,
        },
      };

      dispatch(
        postPolicyApplicationFormNigoRequest({
          clientId,
          clientPolicyApplicationFormId,
          nigoAnalysis: nigoAnalysisResult,
        }),
      );

      actions.saveSuccess();
    },
    [
      clientId,
      currentStep?.clientPolicyApplicationForm?.clientPolicyApplicationFormId,
      state.results,
      state.meta.lastRunTimestamp,
      computed.currentAiResult,
      actions,
      dispatch,
      has70PercentAnalysisTriggered.current,
      has100PercentAnalysisTriggered.current,
    ],
  );

  const { createCompletion: createPartialNIGOCompletion } = useOpenAiCompletion({
    maxPollingAttempts: 100,
    onSuccess: result => {
      logInfo('NigoAnalysisContext', 'Partial NIGO analysis completed', { clientId, formId });
      const parsedResult = parseAiResponse(result.results ?? '');
      if (parsedResult) {
        actions.setAiResult(parsedResult, true);
      }
      actions.analysisComplete();
      saveNigoAnalysis(parsedResult);
    },
    onError: error => {
      logError('NigoAnalysisContext', 'Partial NIGO analysis failed', { clientId, formId }, error);
      actions.analysisError('NIGO analysis failed. Please try again.');
    },
  });

  const { createCompletion: createNIGOCompletion } = useOpenAiCompletion({
    maxPollingAttempts: 100,
    onSuccess: result => {
      logInfo('NigoAnalysisContext', 'Full NIGO analysis completed', { clientId, formId });
      const parsedResult = parseAiResponse(result.results ?? '');
      if (parsedResult) {
        actions.setAiResult(parsedResult, false);
      }
      actions.analysisComplete();
      saveNigoAnalysis(parsedResult);

      // Remove only the questions that were being analyzed
      const editedState = editedQuestions;
      analyzingQuestionsRef.current.forEach((ref: string) => editedState.delete(ref));
      setEditedQuestions(editedState);

      const reCheckState = awaitingRecheck;
      analyzingAwaitingRecheck.forEach((key: string) => reCheckState.delete(key));
      setAwaitingRecheck(reCheckState);

      analyzingQuestionsRef.current.clear();
      setAnalyzingAwaitingRecheck(new Set());
    },
    onError: error => {
      logError('NigoAnalysisContext', 'Full NIGO analysis failed', { clientId, formId }, error);
      actions.analysisError('NIGO analysis failed. Please try again.');
    },
  });

  const getAiPromptType = (stage: '70' | '100'): string => {
    const isPartnerPolicy =
      policyType === FundingPolicyType.jointCareMatters ||
      policyType === FundingPolicyType.jointAssetCare;
    const suffix = isPartnerPolicy ? '-partner' : '';
    return stage === '70' ? `nigo-analysis${suffix}-70` : `nigo-analysis${suffix}-100`;
  };

  const runAIAnalysis = useCallback(
    (
      stage: '70' | '100',
      formData: PolicyFormSubmissionData,
      latestRuleResult?: RuleResult[],
      latestAiResult?: ParsedNigoResponse | null,
    ) => {
      if (!clientId || !clientPolicyApplicationId || !formId) {
        actions.analysisError('Missing required parameters for NIGO analysis');
        return;
      }

      actions.startAnalysis(stage);

      const aiPromptType = getAiPromptType(stage);
      const formAnswers: Record<string, { title: string; answer: PolicyFormSubmissionDataValue }> =
        {};

      const { visibleQuestions } = conditionalService.getFormValidationState(true);

      for (const [questionRef, answer] of Object.entries(formData)) {
        const question = formDefinition.questions[questionRef];
        if (question && visibleQuestions.has(questionRef) && !FILTERED_QUESTION_REFS.includes(questionRef)) {
          formAnswers[questionRef] = {
            title: question.title,
            answer,
          };
        }
      }

      const createCompletion = stage === '70' ? createPartialNIGOCompletion : createNIGOCompletion;

      createCompletion('', {
        model: 'o3',
        aiPromptType,
        clientId,
        metadata: {
          formAnswers,
          formId,
          clientPolicyApplicationId,
          policyType,
          ...(stage === '100' && {
            ruleResult: latestRuleResult || state.results.ruleResult,
            previousAiResult: latestAiResult || computed.currentAiResult,
          }),
        },
      });
    },
    [
      clientId,
      clientPolicyApplicationId,
      formId,
      policyType,
      conditionalService,
      formDefinition,
      actions,
      createPartialNIGOCompletion,
      createNIGOCompletion,
    ],
  );

  const runRulesEvaluation = useCallback(() => {
    const ruleResults = evaluateNigoForPolicy({
      policyType,
      formId: formId ?? '',
      formData: formState.formData,
      state: policyApplication?.stateOfResidenceAbbreviation,
    });

    const { newResolvedIssues, reinstatedIssues } = compareRuleResults(
      ruleResults,
      state.results.ruleResult,
      state.results.resolvedIssues,
    );

    const updatedResolvedIssues = [
      ...state.results.resolvedIssues.filter(resolved => {
        const key = `${resolved.code || ''}:${resolved.refs?.join(',') || ''}`;
        return !reinstatedIssues.has(key);
      }),
      ...newResolvedIssues,
    ];

    actions.setRuleResult(ruleResults, updatedResolvedIssues);
    return ruleResults;
  }, [
    state.results.ruleResult,
    state.results.resolvedIssues,
    policyType,
    formId,
    formState.formData,
    policyApplication?.stateOfResidenceAbbreviation,
    actions,
  ]);

  // Monitor form completion and trigger analysis
  useEffect(() => {
    if (!formState.progress || formState.type === 'loading') return;

    const totalVisibleSubsections = conditionalService.getTotalVisibleSubsections();
    if (totalVisibleSubsections === 0) return;

    const completedCount = formState.progress.completedSubsections.size;
    const completionPercentage = (completedCount / totalVisibleSubsections) * 100;

    // Trigger 70% NIGO analysis
    if (
      completionPercentage >= 70 &&
      completionPercentage <= 80 &&
      !has70PercentAnalysisTriggered.current
    ) {
      has70PercentAnalysisTriggered.current = true;
      runAIAnalysis('70', formState.formData);
    }

    // Trigger 100% NIGO analysis
    if (formState.progress.isFormComplete && !has100PercentAnalysisTriggered.current) {
      has100PercentAnalysisTriggered.current = true;
      const ruleResults = runRulesEvaluation();
      runAIAnalysis('100', formState.formData, ruleResults);
    }
  }, [
    formState.progress,
    formState.type,
    formState.formData,
    conditionalService,
    runRulesEvaluation,
    runAIAnalysis,
    has70PercentAnalysisTriggered.current,
    has100PercentAnalysisTriggered.current,
  ]);

  const findQuestionLocation = useCallback(
    (questionRef: string) => {
      const parents = conditionalService.getQuestionLocation(questionRef);

      if (!parents) return null;

      const sectionIndex = formDefinition.sections.findIndex(
        section => section.id === parents.section.id,
      );

      if (sectionIndex === -1) return null;

      const subsectionIndex = formDefinition.sections[sectionIndex].subsections.findIndex(
        subsection => subsection.id === parents.subsection.id,
      );

      if (subsectionIndex === -1) return null;

      return {
        sectionIndex,
        subsectionIndex,
        subsectionId: parents.subsection.id,
        sectionTitle: parents.section.title,
        subsectionTitle: parents.subsection.title,
      };
    },
    [conditionalService, formDefinition],
  );

  const getLastPosition = useCallback((): Position => {
    const lastSectionIndex = formDefinition.sections.length - 1;
    const lastSection = formDefinition.sections[lastSectionIndex];
    const lastSubsectionIndex = lastSection.subsections.length - 1;

    const section = formDefinition.sections[lastSectionIndex];
    const subsection = section?.subsections[lastSubsectionIndex];

    if (!section || !subsection) {
      logError('NigoAnalysisContext', 'Invalid position for last section/subsection', {
        lastSectionIndex,
        lastSubsectionIndex,
      });
    }

    navigationActions.markVisited(subsection.id);

    return {
      sectionIndex: lastSectionIndex,
      subsectionIndex: lastSubsectionIndex,
      subsectionId: subsection.id,
    };
  }, [formDefinition, navigationActions]);

  const removeFromEditedQuestions = useCallback(
    (relatedQuestions: PolicyFormQuestion[]) => {
      const newEditedQuestions = new Set(editedQuestions);
      relatedQuestions.forEach(q => newEditedQuestions.delete(q.questionRef));
      setEditedQuestions(newEditedQuestions);
    },
    [editedQuestions, setEditedQuestions],
  );

  const saveFormData = useCallback(
    (isFormComplete: boolean) => {
      const dataWithCompositeFields = applyCompositeFields(
        formState.formData,
        formDefinition.questions,
      );

      const cleanedFormData = cleanFormDataForHiddenQuestions(
        dataWithCompositeFields,
        formDefinition,
        parameters,
      );

      savePolicyApplicationFormSubmission(
        clientId ?? '',
        clientPolicyApplicationId ?? '',
        formId ?? '',
        cleanedFormData,
        isFormComplete ? 'completed' : 'in_progress',
        Array.from(formState.visitedSubsections ?? []),
        dispatch,
        forClientId ?? '',
      );
    },
    [
      clientId,
      clientPolicyApplicationId,
      formId,
      formState.formData,
      formState.progress?.visitedSubsections,
      dispatch,
    ],
  );

  const rerunAnalysis = useCallback(() => {
    // Capture the current state of edited questions and issues awaiting recheck
    analyzingQuestionsRef.current = new Set(editedQuestions);
    setAnalyzingAwaitingRecheck(new Set(awaitingRecheck));

    const ruleResults = runRulesEvaluation();
    runAIAnalysis('100', formState.formData, ruleResults);
  }, [
    formState.formData,
    runAIAnalysis,
    runRulesEvaluation,
    editedQuestions,
    awaitingRecheck,
    setAnalyzingAwaitingRecheck,
    analyzingQuestionsRef.current,
  ]);

  const handleSubmissionChange = useCallback(
    (questionRef: string) => (value: PolicyFormSubmissionDataValue) => {
      const newSet = new Set(editedQuestions);
      newSet.add(questionRef);
      setEditedQuestions(newSet);
      navigationActions.updateFormData(questionRef, value);
    },
    [navigationActions],
  );

  const handleJumpToQuestion = useCallback(
    (questionRef: string) => {
      const location = findQuestionLocation(questionRef);
      if (location && navigationActions.navigateTo) {
        setShowNigoAnalysisView(false);
        navigationActions.navigateTo({
          sectionIndex: location.sectionIndex,
          subsectionIndex: location.subsectionIndex,
          subsectionId: location.subsectionId,
        });
      }
    },
    [findQuestionLocation, navigationActions.navigateTo, setShowNigoAnalysisView],
  );

  const handleRerunAnalysis = useCallback(() => {
    saveNigoAnalysis();
    rerunAnalysis();
  }, [saveNigoAnalysis, rerunAnalysis]);

  const toggleIssue = useCallback((key: string) => {
    setexpandedIssues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const handleSaveChanges = useCallback(
    (issueKey: string, relatedQuestions: PolicyFormQuestion[]) => {
      setAwaitingRecheck(prev => new Set(prev).add(issueKey));
      if (expandedIssues.has(issueKey)) {
        toggleIssue(issueKey);
      }
      removeFromEditedQuestions(relatedQuestions);
      saveFormData(false);
    },
    [expandedIssues, toggleIssue, removeFromEditedQuestions, saveFormData, toast],
  );

  const handleCancelChanges = useCallback(
    (relatedQuestions: PolicyFormQuestion[]) => {
      const stepForm = currentStep?.clientPolicyApplicationForm;
      const storedFormData = stepForm?.submissionData;
      if (storedFormData) {
        relatedQuestions.forEach(question => {
          const previousValue = storedFormData[question.questionRef];
          if (previousValue !== undefined) {
            navigationActions.updateFormData(question.questionRef, previousValue);
          }
        });
      }
    },
    [currentStep?.clientPolicyApplicationForm?.submissionData, navigationActions],
  );

  const handleReviewCompleteAndSubmit = useCallback(() => {
    navigationActions.navigateTo(getLastPosition());
    saveFormData(true);
    saveNigoAnalysis();
    setShowNigoAnalysisView(false);
    toast({
      title: '🎉 Form Reviewed',
      description: 'You can now send your form.',
      className: '-translate-y-5 bg-primary text-white',
    });
    navigate(`/clients/${clientId}/policy-application/${clientPolicyApplicationId}`);
  }, [
    clientId,
    clientPolicyApplicationId,
    navigate,
    saveNigoAnalysis,
    toast,
    setShowNigoAnalysisView,
  ]);

  const contextValue: NigoAnalysisContextValue = {
    state,
    data: {
      aiResult: computed.currentAiResult,
      ruleResult: state.results.ruleResult,
      resolvedIssues: state.results.resolvedIssues,
      lastRunTimestamp: state.meta.lastRunTimestamp,
      metadata: {
        formDefinition,
        formData: formState.formData,
        showNigoAnalysisView,
        parameters,
        editedQuestions,
        hasCompletedFormOnce,
        awaitingRecheck,
        analyzingAwaitingRecheck,
      },
    },
    actions: {
      rerunAnalysis,
      handleSubmissionChange,
      setShowNigoAnalysisView,
      onEditedQuestionsChange: setEditedQuestions,
      handleJumpToQuestion,
      handleRerunAnalysis,
      handleReviewCompleteAndSubmit,
      toggleIssue,
      setAccordionValue,
      handleSaveChanges,
      handleCancelChanges,
    },
    ui: {
      isLoading: computed.isLoading,
      error: state.ui.error,
      expandedIssues,
      accordionValue,
    },
  };

  return (
    <NigoAnalysisContext.Provider value={contextValue}>{children}</NigoAnalysisContext.Provider>
  );
}
